import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { GroupBase, OptionsOrGroups } from "react-select";

import useFetch from "src/hooks/useFetch";
import HubInputField from "src/components/inputType";
import AddCasePatientInformation from "../addCase/addCasePatientInformation";
import AddCasePatientAddress from "../addCase/addCasePatientAddress";
import AddCasePatientInsurances from "../addCase/addCasePatientInsurances";

import { CaseContext } from "src/context/caseContext";
import { LoaderContext } from "src/context/loaderContext";
import { addPatientDetails, resetPatientDetails } from "src/helpers/addCase";
import { APIEndpoints } from "src/types/apiTypes";
import { CaseStepOneProps } from "./types";
import { PatientDetailType, PatientDropdownResponse, PracticeDropdownResponse } from "../caseStepTwo/types";




const CaseStepOne: FC<CaseStepOneProps> = ({ control, isVisible, watch, setValue, trigger, getValues, resetField,isCloneCase }): JSX.Element => {
  const [prevPatientId, setPrevPatientId] = useState(getValues("patient_attributes.global_patient_id"));
  const globalPatientId = watch("patient_attributes.global_patient_id");
  const practiceId = watch("doctor_attributes.practice_id");

  const { currentCaseDraft } = useContext(CaseContext)
  // const practiceDropdownRef = useRef<HTMLDivElement | null>(null);
  const { setLoader, hideLoader } = useContext(LoaderContext);

  /**
   * @getPracticeDropdown api to get practice based listing
   * @first initialize the useFetch() hook
   * @second trigger practice listing api to get listing
   */
  const { data: practiceDropdownApiRes } = useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN);



    /**
    * @getPatientListingByDoctorId api to get patient listing from doctor id
    * @first initialize the useFetch() hook
    * @second calling patient api using selected doctor id
    * @third patient api success handler
    */
  const { error: patientListingApiError, data: patientListingApiResponse, mutate: getPatientListing } =
    useFetch<PatientDropdownResponse>(APIEndpoints.PATIENT_DROPDOWN);

  /** @second */
  useEffect(() => {
    if (practiceId && practiceDropdownApiRes) {
      const patientQueryParams = {
        practice_id: practiceId,
      };

      getPatientListing(null, null, patientQueryParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, practiceDropdownApiRes]);

  /** @third */
  useEffect(() => {
    if (patientListingApiResponse || patientListingApiError) {
      setValue("patient_attributes.global_patient_id", globalPatientId || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientListingApiResponse, patientListingApiError]);





  /**
   * @PatientDetailsById
   */
  const { data: patientDetailApiResponse, error: patientDetailApiError, mutate: getPatientById, removeErrorAndData: clearPatientDetails } =
    useFetch<PatientDetailType>(APIEndpoints.GET_PATIENT_DETAILS_BY_ID, {}, {}, false);

  useEffect(() => {
    if (typeof globalPatientId === "number" && prevPatientId !== globalPatientId) {
      setPrevPatientId(0);

      if (globalPatientId !== 0 && patientListingApiResponse) {
        if (!currentCaseDraft) {
          resetPatientDetails(setValue, globalPatientId);
          setLoader();
          clearPatientDetails();
          getPatientById(null, null, { global_patient_id: globalPatientId });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalPatientId, patientListingApiResponse]);

  useEffect(() => {
    if (patientDetailApiResponse || patientDetailApiError) {
      hideLoader();
      if (patientDetailApiResponse) {
        setPrevPatientId(patientDetailApiResponse.data.patient_info.id);
        addPatientDetails(setValue, patientDetailApiResponse);
        setValue("api_pa_approved", patientDetailApiResponse.data.pa_approved)
        if (patientDetailApiResponse.data.pa_approved) {
          resetField("uploaded_documents")
        }
        trigger(["patient_attributes", "insurances_attributes"]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDetailApiResponse, patientDetailApiError]);

  //:- Filling missing data into parent object
  useEffect(() => {
    if (practiceId) {
      const practiceData = practiceDropdownApiRes?.data.find((value) => value.id === practiceId);
      if (practiceData) setValue("practice_name", `${practiceData.name} - ${practiceData.address}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);



  return (
    <div className={!isVisible ? 'd-none' : ''}>
      <div className="mb-3">
        <div className="card mb-3">
          <div className="card-header">
            <h1 className="m-0">Practice Information</h1>
          </div>

          <div className="card-body">
            <div className="row">
              <div className="col-12">
                <Controller
                  name="doctor_attributes.practice_id"
                  control={control}
                  defaultValue={0}
                  render={({ field, fieldState: { error } }) => (
                    <div className={"row"} 
                    //ref={practiceDropdownRef}
                    >
                      <label className={"form-label col-lg-2 col-md-4 col-12 p-lg-0"}>Select a Practice *</label>
                      <div className={"col-lg-10 col-md-8 col-12"}>
                        <HubInputField
                          inputId="doctor_attributes.practice_id"
                          // inputLabel="Select a Practice"
                          inputPlaceholder="Select a Practice..."
                          isRequired={true}
                          isRowBased={true}
                          field={field}
                          error={error}
                          isDropdown={true}
                          isSearchable={false}
                          isTypeAhead
                          dropdownData={
                            (practiceDropdownApiRes && practiceDropdownApiRes.data && practiceDropdownApiRes.data.length > 0 ?
                              practiceDropdownApiRes.data.map((practice) => ({ label: practice.address ? `${practice.name.charAt(0).toUpperCase() + practice.name.slice(1)} - ${practice.address}` : practice.name, value: practice.id }))
                              : []
                            ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                          }
                        />
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>

            {/* <Controller
              name="prescription_attributes.quickstart"
              control={control}
              defaultValue={false}
              render={( { field } ) => (
                <div className="row form-check d-flex mb-2">
                    <label className="form-label col-12 col-md-4 col-lg-2"></label>
                    <div className="col-12 col-md-8 col-lg-10">
                        <input
                            className="form-check-input" type="checkbox"
                            id="prescription_attributes.quickstart"
                            disabled checked={field.value}
                        />
                        <label className="form-check-label" htmlFor="prescription_attributes.quickstart">
                            I, the undersigned Prescriber, hereby authorize the delivery of up to two free doses of Afrezza to the patient's
                            address for next-day delivery, if eligible.
                        </label>
                    </div>
                </div>
              )}
            /> */}

            {/* <Controller
              name="prescription_attributes.written_date"
              control={control}
              defaultValue={format( new Date(), 'MM/dd/yyyy' )}
              render={( { field: { value } } ) => (
                <div className="row form-check d-flex">
                    <label className="form-label col-12 col-md-4 col-lg-2"></label>
                    <div className="col-12 col-md-8 col-lg-10">
                      <input className="form-check-input" type="checkbox" id="flexCheckIndeterminateDisabled" disabled checked/>
                      <label className="form-check-label" htmlFor="flexCheckIndeterminateDisabled">
                        Dispense as Written (written date will be {value})
                      </label>
                    </div>
                </div>
              )}
            />

            <Controller
              name="prescription_attributes.drug_name"
              control={control}
              render={() => <></>}
            /> */}
          </div>
        </div>
      </div>

      {practiceId ? (
        <>
          <Controller
            name="api_pa_approved"
            control={control}
            render={() => <></>}
          />

          <div className="card mb-3">
            <div className="card-header">
              <h1 className="m-0">Patient Information</h1>
            </div>
            <div className="card-body">
              <Controller
                name="patient_attributes.global_patient_id"
                control={control}
                defaultValue={0}
                render={({ field, fieldState: { error } }) => (
                  <div className="row">
                    <label className="form-label col-lg-2 col-md-4 col-12 p-lg-0">Select a Patient *</label>
                    <div className="col-lg-10 col-md-8 col-12">
                      <HubInputField
                        inputId="patient_attributes.global_patient_id"
                        inputPlaceholder="(New Patient)..."
                        isRequired={true}
                        isRowBased={true}
                        field={field}
                        error={error}
                        // inputLabel="Select a Patient"
                        isDropdown={true}
                        isSearchable={true}
                        isTypeAhead
                        dropdownData={
                          (patientListingApiResponse && patientListingApiResponse.data && patientListingApiResponse.data.length > 0 ?
                            [{ value: 0, label: `(New Patient)` }, ...patientListingApiResponse.data.map((patient) => ({ value: patient.id, label: patient.fullname }))]
                            : [{ value: 0, label: `(New Patient)` }]
                          ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                        }
                      />
                    </div>
                  </div>
                )}
              />
              <AddCasePatientInformation control={control} watch={watch} getValues={getValues} setValue={setValue} trigger={trigger} />
            </div>
          </div>
          <AddCasePatientAddress control={control} watch={watch} getValues={getValues} setValue={setValue} />
          <AddCasePatientInsurances control={control} watch={watch} getValues={getValues} setValue={setValue} />
        </>
      ) : null}
    </div>
  );
};

export default CaseStepOne;
